/** One recorded grant or revoke, as `VaultDetail.complimentary` publishes it. Operator-only data. */
export interface GrantActionView {
    operator: string;
    /** The window chosen by name (`OneMonth`, `Indefinite`, …); null on a revoke. */
    term: string | null;
    reason: string;
    occurredUtc: string;
}

/** `VaultDetail.complimentary` — never null; a never-comped vault answers no to all of it. */
export interface ComplimentaryView {
    isComplimentary: boolean;
    /** Null when indefinite — but only meaningful while `isComplimentary` is true. */
    complimentaryUntil: string | null;
    lastGrant: GrantActionView | null;
    lastRevoke: GrantActionView | null;
    revokedEarly: boolean;
}

/** The slice of a vault's detail read the standing is derived from. */
export interface PlanRecordView {
    /** ⚠️ A lapsed or revoked grant still reads Pro · Active — display only, never a decision. */
    planName: string | null;
    /** Null exactly when the vault has no subscription row. Display only otherwise. */
    subscriptionStatus: string | null;
    /**
     * `VaultDetail.planBucket` — `FREE`, `PAYING` or `COMPLIMENTARY`, by the server's entitlement rule (the
     * one the platform breakdown counts with). The only answer to paying-vs-free: a purchase made over a
     * grant and a grant that has ended read identically on every other field. A string so a bucket this
     * build does not know (Relay's `%future added value`) can arrive without a cast.
     */
    planBucket: string;
    /**
     * `VaultDetail.canGrant` — true exactly when the server's `grantPro` would accept this vault (one server
     * predicate, `StoreFunding.IsStoreFunded`, parity-tested against a real grant). The only answer to
     * whether Grant is offered: the bucket cannot say it, and the console never works it out.
     */
    canGrant: boolean;
    complimentary: ComplimentaryView;
}

export type StandingKind = 'free' | 'paying' | 'complimentary' | 'grant-ended' | 'unknown';

export interface ComplimentaryStanding {
    kind: StandingKind;
    planLabel: string;
    /** When a live comp ends; null when it is indefinite or when the vault is not comped. */
    until: string | null;
    /** A live comp with no end — runs until revoked. False whenever the vault is not comped. */
    indefinite: boolean;
    /** How the vault's grant ended, on a `grant-ended` vault; null otherwise. */
    ended: 'lapsed' | 'revoked' | null;
    /** The server's `canGrant`, as published — never re-derived from the bucket, status or history. */
    canGrant: boolean;
    /** Granting a live comp extends its window rather than starting a new one. */
    grantExtends: boolean;
    canRevoke: boolean;
    /** Why Grant is not offered, when it is not. */
    grantUnavailableReason: string | null;
    lastGrant: GrantActionView | null;
    lastRevoke: GrantActionView | null;
    /** The last revoke predates the last grant — it is history, not what happened to the current grant. */
    revokeSupersededByGrant: boolean;
}

/** The windows `VaultGrantAction.term` records, by the server's name for each. */
const TERM_LABELS: Record<string, string> = {
    OneMonth: '1 month',
    ThreeMonths: '3 months',
    TwelveMonths: '12 months',
    Indefinite: 'Indefinite',
};

/** A recorded grant window as copy; an unknown name is shown as recorded rather than hidden. */
export function grantTermLabel(term: string): string {
    return TERM_LABELS[term] ?? term;
}

function occurredAfter(a: GrantActionView, b: GrantActionView): boolean {
    return Date.parse(a.occurredUtc) > Date.parse(b.occurredUtc);
}

/** Why Grant is not offered on a vault a store is paying for. */
const PAYING_REASON = 'A store subscription is paying for this vault — real money always wins.';

/**
 * Why Grant is not offered on any other vault the server says cannot be granted. Covers every case the
 * server refuses without the console telling them apart — telling them apart would mean re-deriving the rule.
 */
const NOT_GRANTABLE_REASON =
    'Complimentary Pro cannot be granted on this vault: a store subscription still holds it (paused, or ' +
    'cancelled but not yet expired), or it has no subscription record to grant against.';

/**
 * What a vault's plan actually is, and which Complimentary Pro actions the console offers on it.
 *
 * ⛔ Paying, free or comped is read from `planBucket`, never inferred from the plan name, the status or
 * the grant history: a grant stays `Active` after it ends, and a purchase over a grant keeps its
 * `lastGrant`, so those fields cannot tell the two apart. The audit fields only label HOW a grant ended.
 *
 * ⛔ Grantability is not the bucket, and is never derived here. Grant is offered exactly when the server's
 * `canGrant` is true (pv-frontend#625): `GrantPro` refuses any vault a store subscription still funds — a
 * paused one and a cancelled one past its period included, both `FREE` — and a vault with no subscription
 * row. No status, bucket or null-row check decides it; a client copy of the funding rule would drift from
 * the server's (the pv-frontend#265 shape). Revoke is a separate question: it needs a live comp.
 */
export function complimentaryStanding(record: PlanRecordView): ComplimentaryStanding {
    const { complimentary, planBucket, canGrant } = record;
    const { lastGrant, lastRevoke } = complimentary;

    // A revoke older than the last grant is a vault revoked and then comped again — never "revoked".
    const revokeSupersededByGrant =
        lastGrant !== null && lastRevoke !== null && occurredAfter(lastGrant, lastRevoke);
    const history = { lastGrant, lastRevoke, revokeSupersededByGrant };
    const unavailableReason = planBucket === 'PAYING' ? PAYING_REASON : NOT_GRANTABLE_REASON;
    const grant = { canGrant, grantUnavailableReason: canGrant ? null : unavailableReason };
    const notComped = {
        ...history,
        ...grant,
        until: null,
        indefinite: false,
        ended: null,
        grantExtends: false,
        canRevoke: false,
    };
    const recordedPlan = [record.planName, record.subscriptionStatus].filter(Boolean).join(' · ');

    switch (planBucket) {
        case 'COMPLIMENTARY':
            return {
                ...history,
                ...grant,
                kind: 'complimentary',
                planLabel: 'Complimentary Pro',
                until: complimentary.complimentaryUntil,
                // `complimentaryUntil: null` means indefinite ONLY here, on a live comp.
                indefinite: complimentary.complimentaryUntil === null,
                ended: null,
                grantExtends: true,
                canRevoke: true,
            };
        case 'PAYING':
            return {
                ...notComped,
                kind: 'paying',
                planLabel: recordedPlan || 'Paying',
            };
        case 'FREE':
            if (lastGrant !== null) {
                const revoked =
                    lastRevoke !== null && !revokeSupersededByGrant && complimentary.revokedEarly;
                return {
                    ...notComped,
                    kind: 'grant-ended',
                    planLabel: 'Free',
                    ended: revoked ? 'revoked' : 'lapsed',
                };
            }
            return {
                ...notComped,
                kind: 'free',
                planLabel: 'Free',
            };
        default:
            return {
                ...notComped,
                kind: 'unknown',
                planLabel: recordedPlan || planBucket,
            };
    }
}
